interface NDEFRecord {
    recordType: string;
    mediaType?: string;
    id?: string;
    data?: DataView;
    encoding?: string;
    lang?: string;
}

interface NDEFMessage {
    records: NDEFRecord[];
}

type ReadingEventListener = (event: { message: NDEFMessage; serialNumber: string }) => void;
type ReadingErrorEventListener = () => void;

/**
 * MockNDEFReader - A mock implementation of the NDEFReader API for development testing
 *
 * This class simulates NFC tag reading functionality without requiring physical NFC hardware.
 * When scan() is called, it displays buttons in the bottom-right corner that developers can
 * click to simulate tag reads with configurable tag UIDs, or a readingerror (an unreadable
 * badge).
 */
export class MockNDEFReader {
    /**
     * Static configuration property for setting a custom tag UID
     */
    public static mockTagUID: string = "14:5B:82:32:EC:4C:81";

    /**
     * Tracks the NFC permission state
     */
    private static permissionState: 'prompt' | 'granted' | 'denied' = 'prompt';

    /**
     * Tracks whether a user interaction has occurred
     * Required to mimic the real Web NFC API behavior
     */
    private static hasUserInteraction: boolean = false;

    /**
     * How many upcoming scan() calls reject with NotReadableError. Tests arm
     * this via `window.NDEFReader.failNextScans(n)` to simulate a transiently
     * unavailable NFC adapter.
     */
    private static scanFailuresRemaining: number = 0;

    public static failNextScans(count: number): void {
        MockNDEFReader.scanFailuresRemaining = count;
    }

    /**
     * Session counters read by tests via `window.NDEFReader`: how many scan()
     * calls succeeded, and how many active sessions were released through
     * their AbortSignal.
     */
    public static scanSessionsStarted: number = 0;
    public static scanSessionsAborted: number = 0;

    private readingListeners: ReadingEventListener[] = [];
    private readingErrorListeners: ReadingErrorEventListener[] = [];
    private mockButton: HTMLButtonElement | null = null;
    private mockErrorButton: HTMLButtonElement | null = null;
    private mockContainer: HTMLDivElement | null = null;
    private mockInput: HTMLInputElement | null = null;

    /**
     * Gets the current permission state
     */
    public static getPermissionState(): 'prompt' | 'granted' | 'denied' {
        return MockNDEFReader.permissionState;
    }

    /**
     * Resets the permission state (useful for testing)
     */
    public static resetPermissionState(): void {
        MockNDEFReader.permissionState = 'prompt';
    }

    /**
     * Sets up listeners to detect user interaction
     * This is called automatically during initialization
     */
    public static setupUserInteractionTracking(): void {
        if (typeof window === 'undefined') {
            return;
        }

        function markInteraction() {
            MockNDEFReader.hasUserInteraction = true;
        }

        // Listen for various user interaction events
        ['touchstart', 'mousedown', 'click', 'keydown'].forEach(eventType => {
            window.addEventListener(eventType, markInteraction, { once: true, capture: true });
        });
    }

    /**
     * Implements the scan() method from the NDEFReader interface
     * Throws NotAllowedError if called without prior user interaction or if permission denied
     * Returns a resolved Promise and creates the mock button UI
     */
    public scan(options?: { signal?: AbortSignal }): Promise<void> {
        const signal = options?.signal;
        if (signal?.aborted) {
            const error = new DOMException(
                'The NFC operation was cancelled.',
                'AbortError'
            );
            return Promise.reject(error);
        }

        // Mimic real Web NFC API behavior: require user interaction
        if (!MockNDEFReader.hasUserInteraction) {
            const error = new DOMException(
                'NFC permission request can only be triggered by a user gesture.',
                'NotAllowedError'
            );
            return Promise.reject(error);
        }

        // Check permission state
        if (MockNDEFReader.permissionState === 'denied') {
            const error = new DOMException(
                'NFC permission was denied.',
                'NotAllowedError'
            );
            return Promise.reject(error);
        }

        // Prompt for permission if not yet granted
        if (MockNDEFReader.permissionState === 'prompt') {
            const granted = MockNDEFReader.promptForPermission();
            if (!granted) {
                const error = new DOMException(
                    'NFC permission was denied.',
                    'NotAllowedError'
                );
                return Promise.reject(error);
            }
        }

        if (MockNDEFReader.scanFailuresRemaining > 0) {
            MockNDEFReader.scanFailuresRemaining -= 1;
            const error = new DOMException(
                'NFC hardware is not readable.',
                'NotReadableError'
            );
            return Promise.reject(error);
        }

        // Aborting the session releases the mock adapter, like the real one
        signal?.addEventListener('abort', () => {
            MockNDEFReader.scanSessionsAborted += 1;
            this.cleanup();
        }, { once: true });

        MockNDEFReader.scanSessionsStarted += 1;
        this.createMockButton();
        return Promise.resolve();
    }

    /**
     * Add event listener for reading or readingerror events
     */
    public addEventListener(type: 'reading' | 'readingerror', listener: ReadingEventListener | ReadingErrorEventListener): void {
        if (type === 'reading') {
            this.readingListeners.push(listener as ReadingEventListener);
        } else if (type === 'readingerror') {
            this.readingErrorListeners.push(listener as ReadingErrorEventListener);
        }
    }

    /**
     * Remove event listener for reading or readingerror events
     */
    public removeEventListener(type: 'reading' | 'readingerror', listener: ReadingEventListener | ReadingErrorEventListener): void {
        if (type === 'reading') {
            this.readingListeners = this.readingListeners.filter(l => l !== listener);
        } else if (type === 'readingerror') {
            this.readingErrorListeners = this.readingErrorListeners.filter(l => l !== listener);
        }
    }

    /**
     * Prompts the user for NFC permission
     * Returns true if granted, false if denied
     */
    private static promptForPermission(): boolean {
        const granted = window.confirm(
            '[Mock NFC] This site wants to use NFC.\n\nAllow NFC access?'
        );

        MockNDEFReader.permissionState = granted ? 'granted' : 'denied';
        return granted;
    }

    /**
     * Create and inject the mock button into the DOM
     */
    private createMockButton(): void {
        if (this.mockContainer) {
            return; // Button already exists
        }

        // Create container (wrapper for absolute positioning)
        this.mockContainer = document.createElement('div');
        Object.assign(this.mockContainer.style, {
            position: 'fixed',
            padding: '15px 15px',
            bottom: '20px',
            right: '20px',
            zIndex: '9999',
            overflow: 'hidden',
            height: '140px',
            width: '620px',
            // The container must never swallow kiosk clicks - only its own
            // controls are interactive.
            pointerEvents: 'none'
        });

        // Create input box container
        const inputBox = document.createElement('div');
        Object.assign(inputBox.style, {
            position: 'absolute',
            right: '-85px',
            top: '10px',
            opacity: '0',
            display: 'flex',
            alignItems: 'center',
            backgroundColor: '#FEFCE8',
            border: '1px solid #e3e4df',
            borderRadius: '8px',
            padding: '12px 16px',
            marginRight: '8px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
            transition: '0.3s ease-in-out',
            whiteSpace: 'nowrap',
            height: '52px',
            width: '272px',
            pointerEvents: 'auto'
        });

        // Create label
        const label = document.createElement('label');
        label.textContent = 'UID';
        label.htmlFor = 'mock-ndef-uid-input';
        Object.assign(label.style, {
            fontSize: '14px',
            fontFamily: 'system-ui, sans-serif',
            fontWeight: '600',
            color: '#282828',
            marginRight: '8px',
            flexShrink: '0'
        });

        // Create input field
        this.mockInput = document.createElement('input');
        this.mockInput.id = 'mock-ndef-uid-input';
        this.mockInput.type = 'text';
        this.mockInput.value = MockNDEFReader.mockTagUID;
        Object.assign(this.mockInput.style, {
            border: '1px solid #e3e4df',
            borderRadius: '4px',
            padding: '4px 8px',
            fontSize: '14px',
            fontFamily: 'system-ui, sans-serif',
            backgroundColor: '#ffffff',
            color: '#282828',
            outline: 'none',
            width: '100%'
        });

        inputBox.appendChild(label);
        inputBox.appendChild(this.mockInput);

        // Create button
        this.mockButton = document.createElement('button');
        this.mockButton.type = 'button';
        this.mockButton.textContent = '🛜 Simulate NFC Tag';
        Object.assign(this.mockButton.style, {
            position: 'absolute',
            right: '10px',
            top: '10px',
            flexGrow: '1',
            backgroundColor: '#FEFCE8',
            color: '#282828',
            border: '1px solid #e3e4df',
            borderRadius: '8px',
            padding: '12px 20px',
            fontSize: '16px',
            fontFamily: 'system-ui, sans-serif',
            fontWeight: '600',
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
            transition: 'all 0.2s ease',
            height: '52px',
            display: 'block',
            pointerEvents: 'auto'
        });

        // Add hover effect to show input box
        this.mockButton.onmouseenter = () => {
            inputBox.style.right = '206px';
            inputBox.style.opacity = '1';
            if (this.mockButton) {
                this.mockButton.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.25)';
            }
        };

        this.mockContainer.onmouseleave = () => {
            inputBox.style.right = '-85px';
            inputBox.style.opacity = '0';
            if (this.mockButton) {
                this.mockButton.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.15)';
            }
        };

        // Add click handler to update UID and trigger 'reading' event
        this.mockButton.onclick = () => {
            if (this.mockInput) {
                MockNDEFReader.mockTagUID = this.mockInput.value;
            }
            this.triggerReadingEvent();
        };

        // Create error button to simulate an unreadable badge (readingerror)
        this.mockErrorButton = document.createElement('button');
        this.mockErrorButton.type = 'button';
        this.mockErrorButton.textContent = '⚠️ Simulate NFC Error';
        Object.assign(this.mockErrorButton.style, {
            position: 'absolute',
            right: '10px',
            top: '70px',
            backgroundColor: '#FEF2F2',
            color: '#282828',
            border: '1px solid #e3e4df',
            borderRadius: '8px',
            padding: '12px 20px',
            fontSize: '16px',
            fontFamily: 'system-ui, sans-serif',
            fontWeight: '600',
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
            transition: 'all 0.2s ease',
            height: '52px',
            display: 'block',
            pointerEvents: 'auto'
        });

        this.mockErrorButton.onclick = () => {
            this.triggerReadingErrorEvent();
        };

        this.mockContainer.appendChild(inputBox);
        this.mockContainer.appendChild(this.mockButton);
        this.mockContainer.appendChild(this.mockErrorButton);
        document.body.appendChild(this.mockContainer);
    }

    /**
     * Trigger a mock 'reading' event with the configured tag UID
     */
    private triggerReadingEvent(): void {
        const mockMessage: NDEFMessage = {
            records: []
        };

        const event = {
            message: mockMessage,
            serialNumber: MockNDEFReader.mockTagUID
        };

        // Notify all registered reading listeners
        this.readingListeners.forEach(listener => {
            listener(event);
        });
    }

    /**
     * Trigger a mock 'readingerror' event, as fired for an unreadable badge
     */
    private triggerReadingErrorEvent(): void {
        this.readingErrorListeners.forEach(listener => {
            listener();
        });
    }

    /**
     * Clean up and remove the mock button from the DOM
     */
    public cleanup(): void {
        if (this.mockContainer && this.mockContainer.parentNode) {
            this.mockContainer.parentNode.removeChild(this.mockContainer);
            this.mockContainer = null;
            this.mockButton = null;
            this.mockErrorButton = null;
            this.mockInput = null;
        }
    }
}

/**
 * Initialize the mock NDEFReader in development mode
 * This should be called once when the app initializes
 */
export function initMockNDEFReader(): void {
    if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
        // Replace the global NDEFReader with our mock implementation
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any).NDEFReader = MockNDEFReader;

        // Set up user interaction tracking to enforce gesture requirement
        MockNDEFReader.setupUserInteractionTracking();

        // Mock navigator.permissions.query for NFC permission
        const originalPermissions = navigator.permissions;
        if (originalPermissions && originalPermissions.query) {
            const originalQuery = originalPermissions.query.bind(originalPermissions);

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (navigator.permissions as any).query = function(permissionDescriptor: PermissionDescriptor): Promise<PermissionStatus> {
                // Only mock the 'nfc' permission
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                if ((permissionDescriptor.name as any) === 'nfc') {
                    // Return the actual permission state
                    const state = MockNDEFReader.getPermissionState();

                    return Promise.resolve({
                        name: 'nfc',
                        state,
                        onchange: null,
                        addEventListener: () => {},
                        removeEventListener: () => {},
                        dispatchEvent: () => true
                    } as PermissionStatus);
                }

                // For all other permissions, use the original implementation
                return originalQuery(permissionDescriptor);
            };
        }

        console.log('[MockNDEFReader] Mock NFC reader and permissions initialized for development');
    }
}

// Call the initialization function immediately
initMockNDEFReader();
